/**
 * Authoritative customer-facing deal value for Portal cards, pipeline, and BOM.
 *
 * Rule: sum(authoritative line.lineTotal) for customer-visible lines only.
 * A line counts only when its system and room/location are customer-visible
 * (and the line bucket is a customer commercial bucket).
 *
 * Visibility source: parsedMetadataJson.dtoolsDisplaySettings
 *   - hiddenOptionKeys (+ customerExposeInternalOptionKeys / withInternalSystemsHiddenByDefault)
 *   - hiddenLocationKeys (stable locationId keys; hidden rooms contribute $0)
 *
 * Does not use header.totalAmount, reconciliation.headerTotal, or qty×unitPrice.
 * Does not mutate procurement / invoice totalAmount.
 */
import {
  filterVisibleDtoolsSystems,
  isCustomerFacingDtoolsSystem,
  withInternalSystemsHiddenByDefault,
} from "./dtoolsOptionVisibility";
import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import { resolveDtoolsSystemContentKey } from "./dtoolsProposalContent";
import { sumCustomerLineTotalsInHierarchySystem } from "./dtoolsReviewNavigation";
import { isServiceLine } from "./dtoolsReviewNavigation";
import { isDtoolsInternalImportSystem } from "./dtoolsInternalSystems";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { isDtoolsJsonProposalSourceValue } from "./proposalSources";
import {
  hasNegotiatedAgreedPrice,
  resolveProposalCustomerPricingDisplay,
  sumProposalItemLineTotals,
  type RetailPricingJson,
} from "./proposalCustomerPricing";
import {
  buildDtoolsLiveProposalViewModel,
  type BuildDtoolsLiveProposalViewOptions,
  type ProposalDbItemForDtoolsView,
  type ProposalHeaderForDtoolsView,
} from "./dtoolsProposalLiveView";

const CACHE_KEY = "customerFacingDealTotal";

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function moneyNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Σ line.lineTotal for customer-visible systems and rooms (and accessories under those),
 * PLUS every priced customer line parked in a D-Tools internal placeholder system
 * ("Unassigned system", "Unknown system", blank) — see collectInternalSystemCustomerSections.
 * Priced lines in Unassigned are never silently dropped from the customer Grand Total.
 */
export function sumVisibleDtoolsSystemsLineTotals(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): number {
  const visible = filterVisibleDtoolsSystems(systems, displaySettings, { audience: "customer" }).map(
    (entry) => entry.system,
  );
  const internalSectionsTotal = sumInternalSystemCustomerSections(systems, displaySettings);
  if (displaySettings?.alternativeOptions === true) {
    const optionTotals = visible.map((system) => sumCustomerLineTotalsInHierarchySystem(system));
    return roundMoney((optionTotals.length ? Math.max(0, ...optionTotals) : 0) + internalSectionsTotal);
  }
  return roundMoney(
    visible.reduce((sum, system) => sum + sumCustomerLineTotalsInHierarchySystem(system), 0) +
      internalSectionsTotal,
  );
}

type DtoolsReviewBlock = DtoolsReviewSystem["systemLevelPrimaries"][number];

/** Buckets that count toward the customer total (same set as sumCustomerLineTotalsInHierarchySystem). */
export const DTOOLS_CUSTOMER_TOTAL_BUCKETS: ReadonlySet<string> = new Set([
  "commercial",
  "discount",
  "service",
  "cable",
]);

/** Customer-billable value of one line: customer bucket, not VO-removed, lineTotal > 0. */
export function dtoolsLineCustomerValue(line: DtoolsReviewBlock["line"]): number {
  if (!DTOOLS_CUSTOMER_TOTAL_BUCKETS.has(line.bucket)) return 0;
  if (line.variationOrderAnnotation?.kind === "removed") return 0;
  return Math.max(0, Number(line.lineTotal) || 0);
}

function blockCustomerValue(block: DtoolsReviewBlock): number {
  return (
    dtoolsLineCustomerValue(block.line) +
    block.accessories.reduce((sum, accessory) => sum + dtoolsLineCustomerValue(accessory), 0)
  );
}

/**
 * Internal placeholder systems whose lines must be added back on top of the visible systems.
 * An internal system that staff explicitly exposed as its own customer section is already counted
 * through the visible systems, so it is not added back (no double count). Hidden-by-default AND
 * explicitly staff-hidden internal systems are always added back — hiding "Unassigned" never
 * removes priced lines from the Grand Total.
 */
function internalSystemsForAddBack(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  return systems.filter(
    (system) =>
      isDtoolsInternalImportSystem(system) &&
      !isCustomerFacingDtoolsSystem(system, displaySettings, "customer"),
  );
}

function filterInternalSystemBlocks(
  system: DtoolsReviewSystem,
  keep: (block: DtoolsReviewBlock) => boolean,
): Pick<DtoolsReviewSystem, "rooms" | "systemLevelPrimaries"> {
  const systemLevelPrimaries = system.systemLevelPrimaries.filter(keep);
  const rooms = system.rooms
    .map((room) => ({ ...room, primaries: room.primaries.filter(keep) }))
    .filter((room) => room.primaries.length > 0);
  return { rooms, systemLevelPrimaries };
}

/** Project-wide service lines remain billable even when D-Tools left them in its internal Unassigned system. */
export function collectGlobalBillableServiceSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  const out: DtoolsReviewSystem[] = [];
  for (const system of internalSystemsForAddBack(systems, displaySettings)) {
    const keep = (block: DtoolsReviewBlock) =>
      isServiceLine(block.line) && block.line.variationOrderAnnotation?.kind !== "removed";
    const { systemLevelPrimaries, rooms } = filterInternalSystemBlocks(system, keep);
    if (systemLevelPrimaries.length || rooms.length) {
      out.push({ ...system, name: "Services", systemLevelPrimaries, rooms });
    }
  }
  return out;
}

export const DTOOLS_UNASSIGNED_OTHER_ITEMS_SYSTEM_ID = "clarity-unassigned-other-items";
export const DTOOLS_UNASSIGNED_OTHER_ITEMS_LABEL = "Other items";

/**
 * Every other priced customer line (products, cables, accessories, child lines) parked in an internal
 * placeholder system, merged into one customer-facing "Other items" section. Zero-priced /
 * internal-only blocks stay hidden.
 */
export function collectUnassignedOtherBillableSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  const rooms: DtoolsReviewSystem["rooms"] = [];
  const systemLevelPrimaries: DtoolsReviewBlock[] = [];
  for (const system of internalSystemsForAddBack(systems, displaySettings)) {
    const keep = (block: DtoolsReviewBlock) =>
      !isServiceLine(block.line) &&
      block.line.variationOrderAnnotation?.kind !== "removed" &&
      blockCustomerValue(block) > 0.004;
    const kept = filterInternalSystemBlocks(system, keep);
    rooms.push(...kept.rooms);
    systemLevelPrimaries.push(...kept.systemLevelPrimaries);
  }
  if (!rooms.length && !systemLevelPrimaries.length) return [];
  return [
    {
      systemId: DTOOLS_UNASSIGNED_OTHER_ITEMS_SYSTEM_ID,
      name: DTOOLS_UNASSIGNED_OTHER_ITEMS_LABEL,
      rooms,
      systemLevelPrimaries,
    },
  ];
}

/**
 * Single source of truth for priced lines in internal placeholder systems: "Services" sections
 * followed by one "Other items" section. Feeds the headline / deal card / Revise total, the customer
 * System Summary and the quotation PDF so all of them agree.
 */
export function collectInternalSystemCustomerSections(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): DtoolsReviewSystem[] {
  return [
    ...collectGlobalBillableServiceSystems(systems, displaySettings),
    ...collectUnassignedOtherBillableSystems(systems, displaySettings),
  ];
}

export function sumInternalSystemCustomerSections(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): number {
  return roundMoney(
    collectInternalSystemCustomerSections(systems, displaySettings).reduce(
      (sum, system) => sum + sumCustomerLineTotalsInHierarchySystem(system),
      0,
    ),
  );
}

/** Services-only portion of the internal add-back (kept for callers that label services separately). */
export function sumGlobalBillableServicesInInternalSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): number {
  return roundMoney(
    collectGlobalBillableServiceSystems(systems, displaySettings).reduce(
      (sum, system) => sum + sumCustomerLineTotalsInHierarchySystem(system),
      0,
    ),
  );
}

/**
 * Partition D-Tools systems using the SAME commercial authority as deal totals.
 *
 * - Visibility: customer-facing filter (+ default-hide internal import systems)
 * - alternativeOptions=true → included = max-valued visible system(s); others visible = offered options
 * - alternativeOptions=false/absent → all customer-visible systems are included (additive)
 * - Hidden systems are excluded from both included and offered analyses
 *
 * Tie-break when multiple visible systems share the max total: lowest optionIndex
 * (filterVisibleDtoolsSystems order), then content-key localeCompare.
 */
export type CustomerFacingSystemPartition = {
  alternativeOptions: boolean;
  includedSystemKeys: string[];
  offeredOptionSystemKeys: string[];
  hiddenSystemKeys: string[];
  /** Max (or sum) of customer-visible system totals — matches deal-total math. */
  authoritativeVisibleTotal: number;
  resolution: "sum_all_visible" | "max_visible_system" | "no_visible_systems";
};

export type CustomerFacingSystemAggregate = {
  systemId?: string | null;
  /** Prefer `name`; `systemName` accepted for Insights line metadata. */
  name?: string | null;
  systemName?: string | null;
  /** Σ customer commercial buckets for this system (commercial|discount|service|cable). */
  customerLineTotal: number;
};

function systemNameFromAggregate(s: CustomerFacingSystemAggregate): string {
  return String(s.name ?? s.systemName ?? "").trim() || "Unassigned system";
}

/**
 * Lightweight partition for Insights (and similar) when only per-system aggregates exist.
 * Mirrors sumVisibleDtoolsSystemsLineTotals selection rules without rebuilding hierarchy.
 */
export function partitionCustomerFacingSystemsByDealAuthority(
  systems: CustomerFacingSystemAggregate[],
  displaySettings?: DtoolsDisplaySettings,
): CustomerFacingSystemPartition {
  const shaped = systems.map((s, optionIndex) => ({
    systemId: s.systemId ?? null,
    name: systemNameFromAggregate(s),
    customerLineTotal: Math.max(0, Number(s.customerLineTotal) || 0),
    optionIndex,
  }));

  const settings = withInternalSystemsHiddenByDefault(shaped, displaySettings ?? {});
  const hiddenSystemKeys: string[] = [];
  const visible: typeof shaped = [];
  for (const s of shaped) {
    const key = resolveDtoolsSystemContentKey(s);
    if (!isCustomerFacingDtoolsSystem(s, settings, "customer")) {
      hiddenSystemKeys.push(key);
      continue;
    }
    visible.push(s);
  }

  if (!visible.length) {
    return {
      alternativeOptions: settings.alternativeOptions === true,
      includedSystemKeys: [],
      offeredOptionSystemKeys: [],
      hiddenSystemKeys,
      authoritativeVisibleTotal: 0,
      resolution: "no_visible_systems",
    };
  }

  if (settings.alternativeOptions === true) {
    const maxTotal = Math.max(...visible.map((s) => s.customerLineTotal));
    const tied = visible
      .filter((s) => Math.abs(s.customerLineTotal - maxTotal) < 0.005)
      .sort((a, b) => a.optionIndex - b.optionIndex || resolveDtoolsSystemContentKey(a).localeCompare(resolveDtoolsSystemContentKey(b)));
    const includedKey = resolveDtoolsSystemContentKey(tied[0]!);
    const offeredOptionSystemKeys = visible
      .map((s) => resolveDtoolsSystemContentKey(s))
      .filter((k) => k !== includedKey);
    return {
      alternativeOptions: true,
      includedSystemKeys: [includedKey],
      offeredOptionSystemKeys,
      hiddenSystemKeys,
      authoritativeVisibleTotal: Math.round(maxTotal * 100) / 100,
      resolution: "max_visible_system",
    };
  }

  return {
    alternativeOptions: false,
    includedSystemKeys: visible.map((s) => resolveDtoolsSystemContentKey(s)),
    offeredOptionSystemKeys: [],
    hiddenSystemKeys,
    authoritativeVisibleTotal: Math.round(visible.reduce((sum, s) => sum + s.customerLineTotal, 0) * 100) / 100,
    resolution: "sum_all_visible",
  };
}

/** Full hierarchy form — totals from sumCustomerLineTotalsInHierarchySystem. */
export function partitionCustomerFacingReviewSystemsByDealAuthority(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): CustomerFacingSystemPartition {
  const aggregates: CustomerFacingSystemAggregate[] = systems.map((system) => ({
    systemId: system.systemId,
    name: system.name,
    customerLineTotal: sumCustomerLineTotalsInHierarchySystem(system),
  }));
  return partitionCustomerFacingSystemsByDealAuthority(aggregates, displaySettings);
}

export function computeDtoolsCustomerFacingDealTotalFromLiveView(
  header: ProposalHeaderForDtoolsView,
  items: ProposalDbItemForDtoolsView[],
  options?: BuildDtoolsLiveProposalViewOptions,
): number | null {
  const viewModel = buildDtoolsLiveProposalViewModel(header, items, options);
  if (!viewModel) return null;
  return sumVisibleDtoolsSystemsLineTotals(viewModel.systems, viewModel.displaySettings);
}

export function readCachedCustomerFacingDealTotal(parsedMetadataJson: unknown): number | null {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return null;
  }
  const raw = (parsedMetadataJson as Record<string, unknown>)[CACHE_KEY];
  if (raw == null) return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? roundMoney(n) : null;
}

export function withCachedCustomerFacingDealTotal(
  parsedMetadataJson: unknown,
  total: number,
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  base[CACHE_KEY] = roundMoney(total);
  return base;
}

/**
 * Living commercial contract total after approved VOs.
 * Prefer composed/Payment-Details revisedContractValue; else header.totalAmount when VO scope exists.
 * Do not use visible equipment line sums — those omit negotiated discounts and can inflate past the living head.
 */
export function resolveLivingCommercialContractTotal(input: {
  /** True when at least one approved VO is in scope for this proposal. */
  hasApprovedVariationOrders: boolean;
  headerTotalAmount?: unknown;
  revisedContractValue?: number | null;
}): number | null {
  if (!input.hasApprovedVariationOrders) return null;
  if (input.revisedContractValue != null && Number.isFinite(Number(input.revisedContractValue))) {
    return roundMoney(Math.max(0, Number(input.revisedContractValue)));
  }
  if (input.headerTotalAmount != null && Number.isFinite(Number(input.headerTotalAmount))) {
    return roundMoney(Math.max(0, Number(input.headerTotalAmount)));
  }
  return null;
}

/**
 * Portal deal value used by proposal cards, pipeline stage totals, and deal overview.
 * Prefer an explicit living commercial contract total (approved VO head) when provided.
 * Negotiated Final Agreed Price uses the same payable authority as the customer proposal.
 * Otherwise prefer live visible sum → cached → header.totalAmount (non-D-Tools / legacy fallback).
 */
export function resolvePortalCustomerFacingDealTotal(input: {
  totalAmount?: unknown;
  parsedMetadataJson?: unknown;
  proposalSource?: string | null;
  liveVisibleTotal?: number | null;
  retailPricingJson?: RetailPricingJson | null;
  invoiceGstEnabled?: boolean | null;
  lineItemTotalSum?: number | null;
  /** Living Commercial Head contract value after approved VOs (authoritative when set). */
  commercialContractTotal?: number | null;
}): number {
  if (input.commercialContractTotal != null && Number.isFinite(Number(input.commercialContractTotal))) {
    return roundMoney(Math.max(0, Number(input.commercialContractTotal)));
  }

  if (
    hasNegotiatedAgreedPrice(input.retailPricingJson) ||
    (input.liveVisibleTotal != null && Number.isFinite(input.liveVisibleTotal))
  ) {
    return resolveProposalCustomerPricingDisplay({
      totalAmount: input.totalAmount,
      retailPricingJson: input.retailPricingJson,
      invoiceGstEnabled: input.invoiceGstEnabled,
      computedProposalTotal: input.liveVisibleTotal,
      lineItemTotalSum: input.lineItemTotalSum,
    }).finalAmountPayable;
  }

  const cached = readCachedCustomerFacingDealTotal(input.parsedMetadataJson);
  if (cached != null) return cached;
  return roundMoney(Math.max(0, moneyNumber(input.totalAmount)));
}

/** Staff-facing proposal value — same resolver as cards, pipeline, and deal workspace. */
export function resolveStaffFacingProposalDealValue(input: {
  totalAmount?: unknown;
  parsedMetadataJson?: unknown;
  proposalSource?: string | null;
  liveVisibleTotal?: number | null;
  retailPricingJson?: RetailPricingJson | null;
  invoiceGstEnabled?: boolean | null;
  items?: Array<{ lineTotal?: unknown }>;
  commercialContractTotal?: number | null;
}): number {
  return resolvePortalCustomerFacingDealTotal({
    totalAmount: input.totalAmount,
    parsedMetadataJson: input.parsedMetadataJson,
    proposalSource: input.proposalSource,
    liveVisibleTotal: input.liveVisibleTotal,
    retailPricingJson: input.retailPricingJson,
    invoiceGstEnabled: input.invoiceGstEnabled,
    lineItemTotalSum:
      input.items && input.items.length > 0 ? sumProposalItemLineTotals(input.items) : null,
    commercialContractTotal: input.commercialContractTotal,
  });
}

export function shouldComputeDtoolsCustomerFacingDealTotal(proposalSource?: string | null): boolean {
  return isDtoolsJsonProposalSourceValue(proposalSource);
}
